import { useEffect, useMemo, useRef, useState } from "react";
import {
  FiEdit2,
  FiFileText,
  FiLayers,
  FiPercent,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import GstSlabModal from "../../components/masters/GstSlabModal";
import HsnCodeModal from "../../components/masters/HsnCodeModal";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

const asList = (response) =>
  Array.isArray(response) ? response : response?.data || [];

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatRuleGroup = (value) =>
  String(value || "GENERAL")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatValueBand = (minimumValue, maximumValue) => {
  const minimum =
    minimumValue === null || minimumValue === undefined
      ? null
      : Number(minimumValue);

  const maximum =
    maximumValue === null || maximumValue === undefined
      ? null
      : Number(maximumValue);

  if (minimum !== null && maximum !== null) {
    return `${formatMoney(minimum)} – ${formatMoney(maximum)}`;
  }

  if (minimum !== null) {
    return `${formatMoney(minimum)} and above`;
  }

  if (maximum !== null) {
    return `Up to ${formatMoney(maximum)}`;
  }

  return "All item values";
};

const purchaseBand = (slab) =>
  formatValueBand(slab.minimumItemValue, slab.maximumItemValue);

const saleBand = (slab) =>
  formatValueBand(slab.saleMinimumItemValue, slab.saleMaximumItemValue);

export default function TaxCatalogPage() {
  const { accessToken } = useAuth();
  const hsnImportInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState("GST");
  const [gstSlabs, setGstSlabs] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPreviewingHsn, setIsPreviewingHsn] = useState(false);
  const [isImportingHsn, setIsImportingHsn] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [showGstModal, setShowGstModal] = useState(false);
  const [showHsnModal, setShowHsnModal] = useState(false);
  const [selectedGstSlab, setSelectedGstSlab] = useState(null);
  const [selectedHsnCode, setSelectedHsnCode] = useState(null);
  const [hsnImportFile, setHsnImportFile] = useState(null);
  const [hsnImportPreview, setHsnImportPreview] = useState(null);

  const loadTaxCatalog = async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError("");

      const [gstResponse, hsnResponse] = await Promise.all([
        apiRequest("/tax-catalog/gst-slabs", { accessToken }),
        apiRequest("/tax-catalog/hsn-codes", { accessToken }),
      ]);

      setGstSlabs(asList(gstResponse));
      setHsnCodes(asList(hsnResponse));
    } catch (requestError) {
      setError(requestError.message || "Unable to load tax catalog.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTaxCatalog();
  }, [accessToken]);

  useEffect(() => {
    setSearch("");
    setStatusFilter("ALL");
    setError("");
    setNotice("");
  }, [activeTab]);

  const filteredGstSlabs = useMemo(() => {
    const query = search.trim().toLowerCase();

    return gstSlabs.filter((slab) => {
      const matchesSearch =
        !query ||
        slab.name?.toLowerCase().includes(query) ||
        slab.ruleGroup?.toLowerCase().includes(query) ||
        slab.description?.toLowerCase().includes(query) ||
        String(slab.rate).includes(query) ||
        purchaseBand(slab).toLowerCase().includes(query) ||
        saleBand(slab).toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && slab.isActive) ||
        (statusFilter === "INACTIVE" && !slab.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [gstSlabs, search, statusFilter]);

  const filteredHsnCodes = useMemo(() => {
    const query = search.trim().toLowerCase();

    return hsnCodes.filter((hsn) => {
      const matchesSearch =
        !query ||
        hsn.code?.toLowerCase().includes(query) ||
        hsn.description?.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && hsn.isActive) ||
        (statusFilter === "INACTIVE" && !hsn.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [hsnCodes, search, statusFilter]);
  const gstPagination = useClientPagination(filteredGstSlabs);
  const hsnPagination = useClientPagination(filteredHsnCodes);

  const activeGstSlabs = gstSlabs.filter((slab) => slab.isActive).length;
  const activeHsnCodes = hsnCodes.filter((hsn) => hsn.isActive).length;

  const ruleGroups = new Set(
    gstSlabs
      .filter((slab) => slab.isActive)
      .map((slab) => slab.ruleGroup || "GENERAL"),
  ).size;

  const handleSaveGstSlab = async (request) => {
    if (request.mode === "GROUP") {
      await apiRequest("/tax-catalog/gst-rule-groups", {
        method: "POST",
        body: request.payload,
        accessToken,
      });
    } else if (selectedGstSlab) {
      await apiRequest(`/tax-catalog/gst-slabs/${selectedGstSlab.id}`, {
        method: "PATCH",
        body: request.payload,
        accessToken,
      });
    } else {
      await apiRequest("/tax-catalog/gst-slabs", {
        method: "POST",
        body: request.payload,
        accessToken,
      });
    }

    await loadTaxCatalog();
    setNotice("GST rule saved successfully.");
    setSelectedGstSlab(null);
  };

  const handleSaveHsnCode = async (payload) => {
    if (selectedHsnCode) {
      await apiRequest(`/tax-catalog/hsn-codes/${selectedHsnCode.id}`, {
        method: "PATCH",
        body: payload,
        accessToken,
      });
    } else {
      await apiRequest("/tax-catalog/hsn-codes", {
        method: "POST",
        body: payload,
        accessToken,
      });
    }

    await loadTaxCatalog();
    setNotice("HSN code saved successfully.");
    setSelectedHsnCode(null);
  };


  const handleImportHsnCodes = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    try {
      setIsPreviewingHsn(true);
      setError("");
      setNotice("");

      const formData = new FormData();
      formData.append("file", file);

      const response = await apiRequest("/tax-catalog/hsn-codes/import/preview", {
        method: "POST",
        body: formData,
        accessToken,
      });

      setHsnImportFile(file);
      setHsnImportPreview(response);
    } catch (requestError) {
      const message = requestError?.details?.message || requestError?.message;
      setError(
        Array.isArray(message)
          ? message.join(" • ")
          : message || "Unable to import HSN codes.",
      );
    } finally {
      setIsPreviewingHsn(false);
    }
  };

  const clearHsnImportPreview = () => {
    setHsnImportFile(null);
    setHsnImportPreview(null);
  };

  const submitHsnImport = async () => {
    if (!hsnImportFile || !hsnImportPreview?.valid) return;

    try {
      setIsImportingHsn(true);
      setError("");

      const formData = new FormData();
      formData.append("file", hsnImportFile);

      const response = await apiRequest("/tax-catalog/hsn-codes/import", {
        method: "POST",
        body: formData,
        accessToken,
      });

      await loadTaxCatalog();
      clearHsnImportPreview();
      setNotice(response?.message || "HSN codes imported successfully.");
    } catch (requestError) {
      const message = requestError?.details?.message || requestError?.message;
      setError(
        Array.isArray(message)
          ? message.join(" • ")
          : message || "Unable to import HSN codes.",
      );
    } finally {
      setIsImportingHsn(false);
    }
  };

  const handleDeleteGstSlab = async (slab) => {
    const confirmed = window.confirm(
      `Delete "${slab.name}" permanently?\n\nThis cannot be undone. Products assigned to this GST rule must be reassigned first.`,
    );

    if (!confirmed) return;

    try {
      setIsDeleting(true);
      setError("");
      setNotice("");

      const response = await apiRequest(`/tax-catalog/gst-slabs/${slab.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadTaxCatalog();
      setNotice(response?.message || "GST rule deleted permanently.");
    } catch (requestError) {
      setError(requestError.message || "Unable to delete GST rule.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteHsnCode = async (hsnCode) => {
    const confirmed = window.confirm(
      `Delete HSN ${hsnCode.code} permanently?\n\nThis cannot be undone. Products assigned to this HSN code must be reassigned first.`,
    );

    if (!confirmed) return;

    try {
      setIsDeleting(true);
      setError("");
      setNotice("");

      const response = await apiRequest(
        `/tax-catalog/hsn-codes/${hsnCode.id}`,
        {
          method: "DELETE",
          accessToken,
        },
      );

      await loadTaxCatalog();
      setNotice(response?.message || "HSN code deleted permanently.");
    } catch (requestError) {
      setError(requestError.message || "Unable to delete HSN code.");
    } finally {
      setIsDeleting(false);
    }
  };

  const isGstTab = activeTab === "GST";

  return (
    <div>
      <input
        ref={hsnImportInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleImportHsnCodes}
        className="hidden"
      />

      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Tax Catalog</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            GST Rules & HSN Codes
          </h1>

          <p className="mt-2 text-gray-500">
            Configure 8-digit HSN classifications plus price-based or fixed GST rules.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!isGstTab && (
            <button
              type="button"
              onClick={() => hsnImportInputRef.current?.click()}
              disabled={isPreviewingHsn || isImportingHsn}
              className="flex h-11 items-center justify-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-5 font-medium text-primary-800 transition hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiUpload />
              {isPreviewingHsn ? "Reading Excel…" : "Import HSN Excel"}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (isGstTab) {
                setSelectedGstSlab(null);
                setShowGstModal(true);
                return;
              }

              setSelectedHsnCode(null);
              setShowHsnModal(true);
            }}
            className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-700 px-5 font-medium text-white transition hover:bg-primary-800"
          >
            <FiPlus />
            {isGstTab ? "Add GST Rule Group" : "Add HSN Code"}
          </button>
        </div>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="GST Bands"
          value={gstSlabs.length}
          description="Configured automatic rate bands"
          icon={<FiPercent />}
          color="bg-primary-50 text-primary-700"
        />

        <StatCard
          label="Active Rule Groups"
          value={ruleGroups}
          description="Product tax categories"
          icon={<FiLayers />}
          color="bg-amber-50 text-amber-700"
        />

        <StatCard
          label="HSN Codes"
          value={hsnCodes.length}
          description="Configured goods classifications"
          icon={<FiFileText />}
          color="bg-blue-50 text-blue-700"
        />

        <StatCard
          label="Active HSN Codes"
          value={activeHsnCodes}
          description={`${activeGstSlabs} active GST bands`}
          icon={<FiFileText />}
          color="bg-violet-50 text-violet-700"
        />

        <StatCard label="Total HSN Codes" value={hsnCodes.length} description="HSN classifications; GST is selected separately" icon={<FiFileText />} color="bg-amber-50 text-amber-700" />
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 px-5 pt-5">
          <div className="flex gap-6">
            <button
              type="button"
              onClick={() => setActiveTab("GST")}
              className={`border-b-2 pb-4 text-sm font-semibold transition ${
                isGstTab
                  ? "border-primary-700 text-primary-700"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              GST Rules
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("HSN")}
              className={`border-b-2 pb-4 text-sm font-semibold transition ${
                !isGstTab
                  ? "border-primary-700 text-primary-700"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              HSN Codes
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-md">
            <FiSearch
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              placeholder={
                isGstTab
                  ? "Search GST rule, group, rate, or band..."
                  : "Search HSN code or description..."
              }
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none transition focus:border-primary-700"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4 outline-none transition focus:border-primary-700"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>

        {error && (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {notice && (
          <div className="m-5 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {notice}
          </div>
        )}

        {isGstTab ? (
          <GstSlabsTable
            isLoading={isLoading}
            isDeleting={isDeleting}
            slabs={gstPagination.pageItems}
            onEdit={(slab) => {
              setSelectedGstSlab(slab);
              setShowGstModal(true);
            }}
            onDelete={handleDeleteGstSlab}
          />
        ) : (
          <HsnCodesTable
            isLoading={isLoading}
            isDeleting={isDeleting}
            codes={hsnPagination.pageItems}
            onEdit={(hsnCode) => {
              setSelectedHsnCode(hsnCode);
              setShowHsnModal(true);
            }}
            onDelete={handleDeleteHsnCode}
          />
        )}
        <ClientPagination pagination={isGstTab ? gstPagination : hsnPagination} label={isGstTab ? "GST rules" : "HSN codes"} />
      </div>

      <GstSlabModal
        open={showGstModal}
        slab={selectedGstSlab}
        existingGroups={gstSlabs.map((item) => item.ruleGroup)}
        onClose={() => {
          setShowGstModal(false);
          setSelectedGstSlab(null);
        }}
        onSave={handleSaveGstSlab}
      />

      <HsnCodeModal
        open={showHsnModal}
        hsnCode={selectedHsnCode}
        onClose={() => {
          setShowHsnModal(false);
          setSelectedHsnCode(null);
        }}
        onSave={handleSaveHsnCode}
      />

      <HsnImportPreviewModal
        open={Boolean(hsnImportPreview)}
        fileName={hsnImportFile?.name}
        preview={hsnImportPreview}
        isImporting={isImportingHsn}
        onClose={clearHsnImportPreview}
        onSubmit={submitHsnImport}
      />

    </div>
  );
}

function StatCard({ label, value, description, icon, color }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">{value}</h2>
          <p className="mt-2 text-xs text-gray-500">{description}</p>
        </div>

        <div className={`rounded-2xl p-3 ${color}`}>{icon}</div>
      </div>
    </div>
  );
}

function StatusBadge({ isActive }) {
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-semibold ${
        isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

function IconButton({ label, onClick, disabled, children, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`grid h-9 w-9 place-items-center rounded-lg border transition disabled:cursor-not-allowed disabled:opacity-40 ${
        danger
          ? "border-red-100 text-red-600 hover:bg-red-50"
          : "border-gray-200 text-gray-600 hover:bg-primary-50 hover:text-primary-700"
      }`}
    >
      {children}
    </button>
  );
}

function GstSlabsTable({ slabs, isLoading, isDeleting, onEdit, onDelete }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[980px] w-full">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              GST Rule
            </th>
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              Rule Group
            </th>
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              Purchase Band
            </th>
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              Sale Band
            </th>
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              Rate
            </th>
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              Status
            </th>
            <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {isLoading ? (
            <EmptyRow colSpan="7" label="Loading GST rules..." />
          ) : slabs.length === 0 ? (
            <EmptyRow colSpan="7" label="No GST rules found." />
          ) : (
            slabs.map((slab) => (
              <tr
                key={slab.id}
                className="border-b border-gray-100 transition hover:bg-gray-50"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary-50 text-primary-700">
                      <FiPercent />
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {slab.name}
                      </h4>
                      <p className="mt-0.5 max-w-xs truncate text-xs text-gray-500">
                        {slab.description || "Automatic billing GST rule"}
                      </p>
                    </div>
                  </div>
                </td>

                <td className="px-6 py-4">
                  <span className="rounded-lg bg-slate-100 px-2.5 py-1.5 font-mono text-xs font-bold text-slate-700">
                    {formatRuleGroup(slab.ruleGroup)}
                  </span>
                </td>

                <td className="px-6 py-4 text-sm font-medium text-gray-700">
                  <p>{purchaseBand(slab)}</p>
                  <p className="mt-1 text-xs font-normal text-gray-400">
                    Before GST
                  </p>
                </td>

                <td className="px-6 py-4 text-sm font-medium text-gray-700">
                  <p>{saleBand(slab)}</p>
                  <p className="mt-1 text-xs font-normal text-gray-400">
                    Including GST
                  </p>
                </td>

                <td className="px-6 py-4">
                  <span className="rounded-xl bg-primary-50 px-3 py-2 font-bold text-primary-800">
                    {Number(slab.rate).toFixed(2)}%
                  </span>
                </td>

                <td className="px-6 py-4">
                  <StatusBadge isActive={slab.isActive} />
                </td>

                <td className="px-6 py-4">
                  <div className="flex justify-end gap-2">
                    <IconButton
                      label={`Edit ${slab.name}`}
                      disabled={isDeleting}
                      onClick={() => onEdit(slab)}
                    >
                      <FiEdit2 />
                    </IconButton>

                    <IconButton
                      label={`Delete ${slab.name}`}
                      disabled={isDeleting}
                      onClick={() => onDelete(slab)}
                      danger
                    >
                      <FiTrash2 />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function HsnCodesTable({
  codes,
  isLoading,
  isDeleting,
  onEdit,
  onDelete,
}) {

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[900px] w-full">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              HSN Code
            </th>
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              Description
            </th>
            <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
              Status
            </th>
            <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {isLoading ? (
            <EmptyRow colSpan="4" label="Loading HSN codes..." />
          ) : codes.length === 0 ? (
            <EmptyRow colSpan="4" label="No HSN codes found." />
          ) : (
            codes.map((hsnCode) => (
              <tr
                key={hsnCode.id}
                className="border-b border-gray-100 transition hover:bg-gray-50"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-700">
                      <FiFileText />
                    </div>

                    <div>
                      <h4 className="font-mono font-bold text-gray-900">
                        {hsnCode.code}
                      </h4>
                      <p className="text-xs text-gray-500">
                        Harmonized System Code
                      </p>
                    </div>
                  </div>
                </td>

                <td className="max-w-xl px-6 py-4 text-sm text-gray-600">
                  {hsnCode.description || "—"}
                </td>


                <td className="px-6 py-4">
                  <StatusBadge isActive={hsnCode.isActive} />
                </td>

                <td className="px-6 py-4">
                  <div className="flex justify-end gap-2">
                    <IconButton
                      label={`Edit HSN ${hsnCode.code}`}
                      disabled={isDeleting}
                      onClick={() => onEdit(hsnCode)}
                    >
                      <FiEdit2 />
                    </IconButton>

                    <IconButton
                      label={`Delete HSN ${hsnCode.code}`}
                      disabled={isDeleting}
                      onClick={() => onDelete(hsnCode)}
                      danger
                    >
                      <FiTrash2 />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}


function EmptyRow({ colSpan, label }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12 text-center text-gray-500">
        {label}
      </td>
    </tr>
  );
}

function HsnImportPreviewModal({
  open,
  fileName,
  preview,
  isImporting,
  onClose,
  onSubmit,
}) {
  if (!open || !preview) return null;

  const errorsByRow = new Map(
    (preview.errors || []).map((error) => [error.rowNumber, error.message]),
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-5 border-b border-gray-100 px-6 py-5">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary-700">
              HSN Excel Preview
            </p>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">
              Review before importing
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {fileName} · Sheet: {preview.sheetName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>

        <div className="grid gap-3 border-b border-gray-100 bg-gray-50 px-6 py-4 sm:grid-cols-3">
          <PreviewStat label="Rows found" value={preview.total} />
          <PreviewStat label="New HSN codes" value={preview.createCount} color="text-emerald-700" />
          <PreviewStat label="Existing codes to update" value={preview.updateCount} color="text-amber-700" />
        </div>

        {!preview.valid && (
          <div className="mx-6 mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Fix the {preview.errors.length} highlighted row{preview.errors.length === 1 ? "" : "s"} in the Excel file, then choose it again. Nothing has been imported.
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-6">
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-[760px] w-full text-sm">
              <thead className="sticky top-0 bg-primary-800 text-left text-xs uppercase tracking-wide text-white">
                <tr>
                  <th className="px-4 py-3">Excel Row</th>
                  <th className="px-4 py-3">HSN Code</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Validation</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => {
                  const error = errorsByRow.get(row.rowNumber);
                  return (
                    <tr key={row.rowNumber} className={error ? "bg-red-50" : "border-t border-gray-100"}>
                      <td className="px-4 py-3 text-gray-500">{row.rowNumber}</td>
                      <td className="px-4 py-3 font-mono font-semibold text-gray-900">{row.code || "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.description || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${row.action === "UPDATE" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                          {row.action === "UPDATE" ? "Update" : "Create"}
                        </span>
                      </td>
                      <td className={`px-4 py-3 ${error ? "font-medium text-red-700" : "text-emerald-700"}`}>
                        {error || "Ready"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={isImporting} className="h-11 rounded-xl border border-gray-200 px-5 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!preview.valid || isImporting}
            className="h-11 rounded-xl bg-primary-700 px-5 font-semibold text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isImporting ? "Submitting import…" : `Submit Import (${preview.total})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewStat({ label, value, color = "text-gray-900" }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
